import { Link } from "react-router-dom";
import { Check, ArrowRight, ArrowUpRight, Phone } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SERVICE_DETAILS, SERVICES, CITIES, BUSINESS } from "@/siteData";
import { trackEvent } from "@/lib/analytics";

const ServicePage = ({ slug }) => {
  const detail = SERVICE_DETAILS[slug];
  const service = SERVICES.find((s) => s.slug === slug);
  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <>
      <Seo title={detail.metaTitle} description={detail.metaDescription} />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12" data-testid={`service-page-${slug}`}>
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-amber-700">
                {detail.eyebrow}
              </p>
              <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl">
                {detail.h1a} <span className="text-amber-600">{detail.h1b}</span>
              </h1>
              {detail.intro.map((p, i) => (
                <p key={i} className="mt-5 max-w-xl text-base leading-relaxed text-slate-600">
                  {p}
                </p>
              ))}
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid={`service-${slug}-estimate-button`}
                  className="inline-flex items-center gap-2 rounded-full bg-amber-600 px-7 py-3.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:scale-105 hover:bg-amber-700"
                >
                  Get a Free Estimate
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={BUSINESS.phoneHref}
                  data-testid={`service-${slug}-call-button`}
                  onClick={() => trackEvent("phone_click", { location: `service-${slug}` })}
                  className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 px-7 py-3 text-sm font-semibold text-slate-900 transition-colors duration-200 hover:bg-slate-900 hover:text-white"
                >
                  <Phone className="h-4 w-4" />
                  {BUSINESS.phone}
                </a>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="absolute -right-4 -top-4 h-full w-full rounded-[2rem] border-2 border-amber-600/40" />
                <img
                  src={service.image}
                  alt={service.imageAlt}
                  loading="eager"
                  className="relative h-80 w-full rounded-[2rem] object-cover shadow-2xl shadow-slate-900/20 sm:h-[440px]"
                />
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-20 grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              What's included
            </h2>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {detail.included.map((item) => (
                <li key={item} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-600/10">
                    <Check className="h-3.5 w-3.5 text-amber-700" />
                  </span>
                  <span className="text-sm leading-relaxed text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600">{detail.closing}</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="rounded-3xl bg-slate-900 p-8 text-white">
              <h2 className="font-display text-xl font-bold">Where we do this work</h2>
              <p className="mt-2 text-sm text-slate-400">
                {detail.eyebrow} across the Kansas City metro:
              </p>
              <ul className="mt-5 space-y-3">
                {CITIES.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/service-areas/${c.slug}`}
                      data-testid={`service-${slug}-city-${c.slug}`}
                      className="group flex items-center justify-between rounded-xl border border-slate-700/60 px-4 py-3 text-sm font-semibold transition-colors duration-200 hover:border-amber-600 hover:text-amber-400"
                    >
                      {c.city}, {c.state}
                      <ArrowUpRight className="h-4 w-4 text-slate-500 transition-colors group-hover:text-amber-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        {detail.gallery && (
          <Reveal className="mt-16">
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {detail.galleryTitle}
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
              {detail.galleryIntro}
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {detail.gallery.map((g, i) => (
                <img
                  key={i}
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  data-testid={`service-${slug}-gallery-image-${i}`}
                  className={`h-64 w-full rounded-2xl object-cover shadow-sm transition-transform duration-300 hover:scale-[1.02] ${
                    i === 0 ? "sm:col-span-2 sm:h-80" : ""
                  }`}
                />
              ))}
            </div>
          </Reveal>
        )}

        <Reveal className="mt-16">
          <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
            Related services
          </h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {others.map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                data-testid={`service-${slug}-related-${s.slug}`}
                className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-amber-600/50 hover:shadow-lg"
              >
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-900">{s.title}</h3>
                  <p className="mt-1 text-sm text-slate-600">{s.short}</p>
                </div>
                <ArrowUpRight className="h-5 w-5 shrink-0 text-amber-600 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default ServicePage;
